import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    target?.addEventListener('click', this.#handleColorReselect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  /**
   * Vrai dès qu'une sélection de variante a été lancée depuis cette galerie.
   * Sert à distinguer un clic qui change de coloris d'un clic sur le coloris
   * déjà coché, qui ne déclenche aucune sélection.
   */
  #selectionPending = false;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by moving or replacing the current media gallery.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    this.#selectionPending = true;

    // Seul un choix de coloris déplace la galerie. Un changement de taille, dont
    // celui que `default-variant.js` simule au chargement, la laisse en place.
    const { optionValueId = '' } = event.detail ?? {};
    const selectedInput = optionValueId
      ? event.target.querySelector(`input[data-option-value-id="${CSS.escape(String(optionValueId))}"]`)
      : null;
    const colorChosen = selectedInput instanceof HTMLInputElement && this.#isColorValue(selectedInput.value);

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        const colorSlide = newMediaGallery.getAttribute('data-color-slide');

        // Sinzem : quand la nouvelle galerie porte exactement les mêmes slides,
        // on garde celle qui est affichée au lieu de la remplacer. Les visuels
        // de variante étant tenus hors de la galerie, c'est le cas de toute
        // sélection sur la fiche. Remplacer la galerie la remettait sur sa
        // première slide à chaque changement de taille, et faisait apparaître
        // cette première slide un instant avant la photo du coloris.
        const currentSlideIds = this.#slideIds(this);
        if (currentSlideIds && currentSlideIds === this.#slideIds(newMediaGallery)) {
          if (colorSlide == null) {
            delete this.dataset.colorSlide;
          } else {
            this.dataset.colorSlide = colorSlide;
          }

          if (colorChosen && colorSlide) {
            this.slideshow?.select(parseInt(colorSlide, 10));
          }
          return;
        }

        // Galerie différente : le serveur l'ouvre sur sa première slide. Si le
        // client vient de choisir un coloris, on l'amène sur sa photo avant
        // l'insertion, pour que le slideshow s'ouvre directement dessus.
        if (colorChosen && colorSlide) {
          newMediaGallery.querySelector('[ref="slideshow"]')?.setAttribute('initial-slide', colorSlide);
        }

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        this.#selectionPending = false;
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Sinzem : un clic sur le coloris déjà coché ramène la galerie sur sa photo.
   *
   * Un bouton radio déjà coché ne déclenche pas de changement, donc aucune
   * sélection de variante. Le changement, quand il a lieu, est dispatché de
   * façon synchrone juste après le clic : on attend la tâche suivante pour
   * savoir si ce clic en a produit un.
   *
   * @param {MouseEvent} event - The click event.
   */
  #handleColorReselect = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
    if (!input.closest('variant-picker') || input.closest('product-card')) return;

    if (!this.#isColorValue(input.value)) return;

    this.#selectionPending = false;

    setTimeout(() => {
      if (this.#selectionPending || !this.isConnected) return;

      const colorSlide = parseInt(this.dataset.colorSlide ?? '', 10);
      if (Number.isNaN(colorSlide)) return;

      this.slideshow?.select(colorSlide);
    });
  };

  /**
   * @param {string} value - Une valeur d'option de variante.
   * @returns {boolean} Vrai si la valeur est un coloris relié à une photo de la galerie.
   */
  #isColorValue(value) {
    return (this.dataset.colorValues ?? '').split(',').filter(Boolean).includes(value);
  }

  /**
   * @param {Element} gallery - Une galerie, affichée ou issue du HTML récupéré.
   * @returns {string} Les identifiants de ses slides, dans l'ordre.
   */
  #slideIds(gallery) {
    return Array.from(gallery.querySelectorAll('[ref="slideshow"] slideshow-slide'), (slide) =>
      slide.getAttribute('slide-id')
    ).join(',');
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
